import Button from "common/Buttons/Button";
import Text from "common/Typography/Text";
import {
  BASE_REGISTER_PATH,
  LARGE_MOBILE,
  LOGIN_PATH,
  RESET_PASSWORD_PATH,
} from "common/constants";
import Flex from "common/Boxes/Flex";
import React, { CSSProperties, useContext } from "react";
import styled, { useTheme } from "styled-components";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery.ts";
import Link from "common/Typography/Link.tsx";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import {
  AUTH_FORGOT_PASSWORD_LINK_ID,
  AUTH_LOGIN_LINK_ID,
  AUTH_LOGIN_SUBMIT_BUTTON_ID,
  AUTH_REGISTER_LINK_ID,
  AUTH_REGISTER_SUBMIT_BUTTON_ID,
} from "testIds.ts";

interface AuthFormFooterProps {
  buttonLoading: boolean;
  buttonText?: string;
  buttonStyle?: CSSProperties;
  isRegister?: boolean;
  /** New look: the login form shows the link next to "Remember me". */
  hideForgotPassword?: boolean;
}

const AuthFormFooter = ({
  buttonLoading,
  buttonText: propsButtonText,
  buttonStyle,
  isRegister,
  hideForgotPassword,
}: AuthFormFooterProps) => {
  const { isNewLook } = useTheme();
  const { isExternalConnectAccount } =
    useContext(ExternalConnectAccountContext) || {};
  const { isWhitelabel } = useBranding();
  const { appendQuery } = useHistoryWithQuery();
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);
  // The new look writes these in sentence case ("Log in", "account").
  const login = t(isNewLook ? "footer.login_new_look" : "footer.login");
  const buttonText = isRegister ? t("footer.register") : login;
  const linkText = isRegister ? login : t("footer.register");
  const text = isRegister
    ? t("footer.already_have_account")
    : t(
        isNewLook
          ? "footer.dont_have_account_new_look"
          : "footer.dont_have_account"
      );

  const onLoginOrRegisterClick = () => {
    const path = isRegister ? LOGIN_PATH : BASE_REGISTER_PATH;
    return appendQuery(undefined, undefined, undefined, path);
  };

  const onResetPasswordClick = () => {
    return appendQuery(undefined, undefined, undefined, RESET_PASSWORD_PATH);
  };

  return (
    <Flex $flexDirection="column">
      <Button
        type="primary"
        $size="l"
        htmlType="submit"
        loading={buttonLoading}
        data-testid={
          isRegister
            ? AUTH_REGISTER_SUBMIT_BUTTON_ID
            : AUTH_LOGIN_SUBMIT_BUTTON_ID
        }
        style={{
          width: "100%",
          ...buttonStyle,
        }}
      >
        {propsButtonText || buttonText}
      </Button>
      <Flex $flexDirection="column" style={{ marginTop: isNewLook ? 16 : 20 }}>
        {!isRegister && !hideForgotPassword && (
          <Link
            onClick={onResetPasswordClick}
            data-testid={AUTH_FORGOT_PASSWORD_LINK_ID}
            style={{ marginBottom: 12 }}
          >
            {t("footer.forgot_password")}
          </Link>
        )}
        {(!isWhitelabel || isExternalConnectAccount) &&
          (isNewLook ? (
            <SwitchText type="secondary">
              {text}{" "}
              <Link
                onClick={onLoginOrRegisterClick}
                data-testid={
                  isRegister ? AUTH_LOGIN_LINK_ID : AUTH_REGISTER_LINK_ID
                }
              >
                {linkText}
              </Link>
            </SwitchText>
          ) : (
            <Text>
              {text}{" "}
              <Link
                onClick={onLoginOrRegisterClick}
                data-testid={
                  isRegister ? AUTH_LOGIN_LINK_ID : AUTH_REGISTER_LINK_ID
                }
              >
                {linkText}
              </Link>
            </Text>
          ))}
      </Flex>
    </Flex>
  );
};

export default AuthFormFooter;

// New look: "Already have an account? Log in". On phones the link gets a
// thumb-sized hit area (the padding is cancelled out, so nothing moves).
const SwitchText = styled(Text)`
  @media (max-width: ${LARGE_MOBILE}) {
    a {
      display: inline-block;
      padding: 12px 4px;
      margin: -12px -4px;
    }
  }
`;
